import { StorageAdapter } from "../storage";
import { RawFlag, RawFlagOptIn, RawFlags } from "./flags";
import { isValidFlagStateVersion } from "./flagStateVersion";

const DEFAULT_STORAGE_KEY = "__reflag_fetched_flags";

function parseOptIn(optIn: any): RawFlagOptIn | null | undefined {
  if (typeof optIn === "undefined") return undefined;
  if (optIn === null) return null;
  if (!isObject(optIn)) return;

  if (
    typeof optIn.userOptedIn !== "boolean" ||
    typeof optIn.companyOptedIn !== "boolean" ||
    typeof optIn.isOptedIn !== "boolean" ||
    typeof optIn.name !== "string" ||
    !(typeof optIn.description === "string" || optIn.description === null)
  ) {
    return;
  }

  return {
    userOptedIn: optIn.userOptedIn,
    companyOptedIn: optIn.companyOptedIn,
    isOptedIn: optIn.isOptedIn,
    name: optIn.name,
    description: optIn.description,
  };
}

function isEvaluationErrorArray(
  value: any,
): value is NonNullable<RawFlag["evaluationErrors"]> {
  return (
    Array.isArray(value) &&
    value.every(
      (error) =>
        isObject(error) &&
        typeof error.code === "string" &&
        typeof error.field === "string" &&
        typeof error.message === "string" &&
        (typeof error.operator === "undefined" ||
          typeof error.operator === "string"),
    )
  );
}

interface cacheEntry {
  expireAt: number;
  staleAt: number;
  flags: RawFlags;
  flagStateVersion?: number;
}

// Parse and validate an API flags response
export function parseAPIFlagsResponse(flagsInput: any): RawFlags | undefined {
  if (!isObject(flagsInput)) {
    return;
  }

  const flags: RawFlags = {};
  for (const key in flagsInput) {
    const flag = flagsInput[key];
    if (!isObject(flag)) return;

    const optIn = parseOptIn(flag.optIn);

    if (
      typeof flag.isEnabled !== "boolean" ||
      flag.key !== key ||
      typeof flag.targetingVersion !== "number" ||
      (flag.config && typeof flag.config !== "object") ||
      (flag.missingContextFields &&
        !Array.isArray(flag.missingContextFields)) ||
      (flag.ruleEvaluationResults &&
        !Array.isArray(flag.ruleEvaluationResults)) ||
      (typeof flag.evaluationErrors !== "undefined" &&
        !isEvaluationErrorArray(flag.evaluationErrors)) ||
      (typeof flag.config?.evaluationErrors !== "undefined" &&
        !isEvaluationErrorArray(flag.config.evaluationErrors)) ||
      (typeof flag.optInEnabled !== "undefined" &&
        typeof flag.optInEnabled !== "boolean") ||
      (typeof flag.optIn !== "undefined" && typeof optIn === "undefined")
    ) {
      return;
    }

    flags[key] = {
      isEnabled: flag.isEnabled,
      targetingVersion: flag.targetingVersion,
      key,
      config: flag.config,
      missingContextFields: flag.missingContextFields,
      ruleEvaluationResults: flag.ruleEvaluationResults,
      evaluationErrors: flag.evaluationErrors,
      ...(typeof flag.optInEnabled !== "undefined" && {
        optInEnabled: flag.optInEnabled,
      }),
      ...(typeof flag.optIn !== "undefined" && { optIn }),
    };
  }

  return flags;
}

export interface CacheResult {
  flags: RawFlags;
  stale: boolean;
  flagStateVersion?: number;
}

export class FlagCache {
  private storage: StorageAdapter;
  private readonly storageKey: string;
  private readonly staleTimeMs: number;
  private readonly expireTimeMs: number;

  constructor({
    storage,
    staleTimeMs,
    expireTimeMs,
  }: {
    storage: StorageAdapter;
    staleTimeMs: number;
    expireTimeMs: number;
  }) {
    this.storage = storage;
    this.storageKey = DEFAULT_STORAGE_KEY;
    this.staleTimeMs = staleTimeMs;
    this.expireTimeMs = expireTimeMs;
  }

  async set(
    key: string,
    {
      flags,
      flagStateVersion,
    }: {
      flags: RawFlags;
      flagStateVersion?: number;
    },
  ) {
    let cacheData: CacheData = {};

    try {
      const cachedResponseRaw = await this.storage.getItem(this.storageKey);
      if (cachedResponseRaw) {
        cacheData = validateCacheData(JSON.parse(cachedResponseRaw)) ?? {};
      }
    } catch {
      // ignore errors
    }

    cacheData[key] = {
      expireAt: Date.now() + this.expireTimeMs,
      staleAt: Date.now() + this.staleTimeMs,
      flags,
      flagStateVersion,
    } satisfies cacheEntry;

    cacheData = Object.fromEntries(
      Object.entries(cacheData).filter(([_k, v]) => v.expireAt > Date.now()),
    );

    await this.storage.setItem(this.storageKey, JSON.stringify(cacheData));

    return cacheData;
  }

  async get(key: string): Promise<CacheResult | undefined> {
    try {
      const cachedResponseRaw = await this.storage.getItem(this.storageKey);
      if (cachedResponseRaw) {
        const cachedResponse = validateCacheData(JSON.parse(cachedResponseRaw));
        if (
          cachedResponse &&
          cachedResponse[key] &&
          cachedResponse[key].expireAt > Date.now()
        ) {
          return {
            flags: cachedResponse[key].flags,
            stale: cachedResponse[key].staleAt < Date.now(),
            flagStateVersion: cachedResponse[key].flagStateVersion,
          };
        }
      }
    } catch {
      // ignore errors
    }
    return;
  }
}

type CacheData = Record<string, cacheEntry>;
function validateCacheData(cacheDataInput: any) {
  if (!isObject(cacheDataInput)) {
    return;
  }

  const cacheData: CacheData = {};
  for (const key in cacheDataInput) {
    const cacheEntry = cacheDataInput[key];
    if (!isObject(cacheEntry)) return;

    if (
      typeof cacheEntry.expireAt !== "number" ||
      typeof cacheEntry.staleAt !== "number" ||
      (cacheEntry.flags && !parseAPIFlagsResponse(cacheEntry.flags)) ||
      (typeof cacheEntry.flagStateVersion !== "undefined" &&
        !isValidFlagStateVersion(cacheEntry.flagStateVersion))
    ) {
      return;
    }

    cacheData[key] = {
      expireAt: cacheEntry.expireAt,
      staleAt: cacheEntry.staleAt,
      flags: cacheEntry.flags,
      flagStateVersion: cacheEntry.flagStateVersion,
    };
  }
  return cacheData;
}

/**
 * Check if the given item is an object.
 *
 * @param item - The item to check.
 * @returns `true` if the item is an object, `false` otherwise.
 **/
export function isObject(item: any): item is Record<string, any> {
  return (item && typeof item === "object" && !Array.isArray(item)) || false;
}
